import PropTypes from 'prop-types';
import React, {
  useState,
  useEffect,
  useRef,
} from 'react';

import { useMutation }    from '@apollo/client';
import { Map, List, Set } from 'immutable';
import isEmpty            from 'lodash/isEmpty';
import ReactHtmlParser    from 'react-html-parser';
import recordJS           from 'records';

import Alert            from '@mui/material/Alert';
import AlertTitle       from '@mui/material/AlertTitle';
import Container        from '@mui/material/Container';
import LoadingButton    from '@mui/lab/LoadingButton';
import Grid             from '@mui/material/Grid';
import TextField        from '@mui/material/TextField';
import Typography       from '@mui/material/Typography';
import FormGroup        from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox         from '@mui/material/Checkbox';
import IntlTelInput     from 'components/Generic/IntlTelInput';

import { errorSelector } from 'helpers/static';
import { cleanName }     from 'helpers/string';

import { CANDIDATE_APPLICATION_APPLY } from 'queries/candidate/apply';

import Display               from 'components/Generic/Display';
import CommunicationChannels from 'components/LandingPage/CommunicationChannels';
import PrivacyPolicyPopUp    from './PrivacyPolicyPopUp';

function LandingPageApply(props) {
  const { uri, navigate, theme, querystring, source, campaign, company } = props;

  const [state, setState] = useState(new Map());

  const stateRef = useRef();

  // Used in `setCandidateInput` because of an edge case when toggling the country code dropdown, it resets the `candidate` variable.
  stateRef.current = state;

  const candidate = state.get('candidate', new Map({
    campaignId:        `${campaign.id}`,
    firstName:         '',
    lastName:          '',
    email:             '',
    msisdn:            '',
    source:            source,
    profileAttributes: new List([
      { key: 'others.whatsapp_optin', value: 'No' },
    ]),
    preferredCommunicationChannels: new List(),
  }));

  const dataErrors    = state.get('dataErrors', new Map()); // return error from astraeus
  const errors        = state.get('errors', new Map()); // errors from UI validation
  const channelErrors = state.get('channelErrors', new Set());
  const privacyPolicy = company.parameters.privacyPolicyLink && !company.get('landingPagePopUpScreen') || state.get('privacyPolicy', false);
  const isSubmittable = state.get('isSubmittable', false);
  const whatsappOptin = state.get('whatsappOptin', false);

  const [submitApplication, { loading, error, data }] = useMutation(CANDIDATE_APPLICATION_APPLY, { errorPolicy: 'all' });

  const CHANNEL_VALIDATIONS = {
    email:    'email',
    sms:      'msisdn',
    whatsapp: 'msisdn',
  };

  const CHANNEL_VALIDATIONS_COPIES = {
    msisdn: t('eos.landing_page.label.phone_number'),
    email:  t('eos.landing_page.label.email')
  }

  const showPopUp = !privacyPolicy && !!company.get('landingPagePopUpScreen');

  useEffect(() => {
    const noErrors = errors.filter(err => err.length > 0).size === 0;

    let channelErrorsValues = new Set();
    let whatsappOptInValue = false;

    const channels = candidate.get('preferredCommunicationChannels');
    if (channels.size) {
      // Validate sms or whatsapp and presence of phone number
      // Validate email and presence of email
      const errorsToShow = [];
      channels.forEach(
        (channel) => {
          if (CHANNEL_VALIDATIONS[channel] && !candidate.get(CHANNEL_VALIDATIONS[channel])) {
            // Error, phone or email is missing according to the channels selected
            // Set error in state.
            errorsToShow.push(CHANNEL_VALIDATIONS[channel]);
          }
        },
      );

      channelErrorsValues = new Set(errorsToShow);
      whatsappOptInValue = channels.includes('whatsapp');
    }

    if (
      noErrors
      && (!isEmpty(candidate.get('email')) || !isEmpty(candidate.get('msisdn')))
      && privacyPolicy
      && channelErrorsValues.size === 0
    ) {
      setState(state.set('isSubmittable', true)
        .set('whatsappOptin', whatsappOptInValue)
        .set('channelErrors', channelErrorsValues));
    } else {
      setState(state.set('isSubmittable', false)
        .set('whatsappOptin', whatsappOptInValue)
        .set('channelErrors', channelErrorsValues));
    }
  }, [candidate, privacyPolicy]);

  useEffect(() => {
    const profileAttributes = candidate.get('profileAttributes', new List());
    const indexOfWhatsapp   = profileAttributes.findIndex((p) => p.key === 'others.whatsapp_optin');

    if (indexOfWhatsapp >= 0 && profileAttributes.getIn([indexOfWhatsapp, 'value']) !== whatsappOptin) {
      const optInValue = whatsappOptin ? 'Yes' : 'No';
      setState(state.set('candidate', candidate.set('profileAttributes', profileAttributes.setIn([indexOfWhatsapp, 'value'], optInValue))));
    }
  }, [whatsappOptin]);

  useEffect(() => {
    if (data) {
      const record    = recordJS({ data });
      const applicant = record.getIn(['data', 'candidate', 'applications', 'applications', 0]);

      const recordErrors = record.getIn(['data', 'candidate', 'applications', 'errors'], new Map()) || record.getIn(['data', 'candidate', 'applications', 'applications', 'errors'], new Map());

      if (typeof recordErrors === 'object' && recordErrors.size > 0) {
        setState(state.set('dataErrors', recordErrors));
        return;
      }

      // had to remove /apply causing some errors
      if (applicant && applicant.bearerToken.length > 0) {
        let candidateLocation = `${uri.replace('/apply', '')}/candidate/${applicant.bearerToken}`;

        if (querystring.length > 0) {
          candidateLocation = `${candidateLocation}?${querystring}`;
        }

        // console.log('mutation', applicant.bearerToken, data, candidateLocation);
        if (candidate.get('preferredCommunicationChannels').includes('messenger')) {
          // Redirect to messenger url
          window.open(`${company.get('messengerUrl')}?ref=optin.${applicant.id}`);
        }
        navigate(candidateLocation);
      }
    }
  }, [data]);

  function setCandidateInput(element) {
    setState(state.set('candidate', stateRef.current.get('candidate').set(element.target.name, element.target.value))
      .set('errors', errors.set(element.target.name, errorSelector(element))));
  }

  function setPrivacyPolicy() {
    setState(state.set('privacyPolicy', !state.get('privacyPolicy')));
  }

  function onSubmit() {
    const applicant = {
      firstName:          cleanName(candidate.get('firstName')),
      lastName:           cleanName(candidate.get('lastName')),
      campaignId:         candidate.get('campaignId'),
      source:             candidate.get('source'),
      profileAttributes:  candidate.get('profileAttributes').toJS(),
    };

    if (!isEmpty(candidate.get('email'))) {
      applicant.email = candidate.get('email');
    }

    if (!isEmpty(candidate.get('msisdn'))) {
      applicant.msisdn = candidate.get('msisdn');
    }

    if (candidate.get('preferredCommunicationChannels').size) {
      applicant.preferredCommunicationChannels = candidate.get('preferredCommunicationChannels').toJS();
    }

    submitApplication({
      variables:
      {
        applications: [applicant],
      },
    });
  }

  function errorMessages() {
    let errs;
    // return error from apollo
    if (error) {
      errs = new List(error.graphQLErrors).map(
        (err) => (
          <li key={err}>
            {err.extensions ? err.extensions.problems[0].message : err.message}
          </li>
        ),
    );
    }

    if (!isEmpty(dataErrors.toJS())) {
      errs = dataErrors.get('0').map((err) => <li key={err}>{err}</li>);
    }

    return (
      <ul style={{ padding: 0 }}>
        {errs}
      </ul>
    );
  }

  const onCommunicationChannelsChange = (channels) => {
    setState(state.set('candidate', candidate.set('preferredCommunicationChannels', new List(channels))));
  };

  if ( error ) console.log('error', error, 'graphQLErrors', error.graphQLErrors);

  const fieldStyle = {
    width: '100%',
    'input:-webkit-autofill': {
      boxShadow: '0 0 0 50px white inset',
    },
    'input:-webkit-autofill:focus': {
      boxShadow: '0 0 0 50px white inset',
    },
  };

  return (
    <Container>
      <Grid container>
        <Grid item xs={12} sx={{
          mt: 4,
          mb: 5,
          [theme.breakpoints.down('sm')]: {
            textAlign: 'center'
          }
        }}>
          <Typography variant="h1" sx={{
            fontWeight: 'medium',
            fontSize: '1.5rem',
            [theme.breakpoints.down('sm')]: {
              textAlign: 'center',
              color: 'primary.main'
            }
          }}>
            {campaign.title}
          </Typography>
        </Grid>

        <Grid container item spacing={2}>
          <Grid item md={6} xs={12}>
            <TextField
              required
              id="first-name"
              label={t('eos.landing_page.label.first_name')}
              name="firstName"
              error={errors.get('firstName') && errors.get('firstName').length > 0}
              defaultValue={candidate.getIn(['candidate', 'firstName']) || ''}
              onChange={(e) => setCandidateInput(e)}
              // helperText={errors.get('firstName') && errors.getIn(['firstName', 0])}
              disabled={loading === true}
              sx={fieldStyle}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <TextField
              required
              id="last-name"
              label={t('eos.landing_page.label.last_name')}
              name="lastName"
              error={errors.get('lastName') && errors.get('lastName').length > 0}
              defaultValue={candidate.getIn(['candidate', 'lastName']) || ''}
              onChange={(e) => setCandidateInput(e)}
              // helperText={errors.get('lastName') && errors.getIn(['lastName', 0])}
              disabled={loading === true}
              sx={fieldStyle}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <TextField
              required
              id="email"
              label={t('eos.landing_page.label.email')}
              name="email"
              error={errors.get('email') && errors.get('email').length > 0}
              defaultValue={candidate.getIn(['candidate', 'email']) || ''}
              onChange={(e) => setCandidateInput(e)}
              // helperText={errors.get('email') && errors.getIn(['email', 0])}
              disabled={loading === true}
              type="email"
              sx={fieldStyle}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <IntlTelInput
              id="phone-number"
              name="msisdn"
              label={t('eos.landing_page.label.phone_number')}
              onChange={setCandidateInput}
              disabled={loading === true}
              initialCountry={campaign.countryShortCode}
            />
          </Grid>
        </Grid>

        <Grid item xs={12} sx={{ mt: 3 }}>
          <CommunicationChannels onChange={onCommunicationChannelsChange} company={company} />
          <Display if={channelErrors.size}>
            <Grid sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <ul key={error} style={{ padding: 0 }} data-testid="channel_errors_container">
                {
                  channelErrors.map(
                    (channelError) => (
                      <li key={channelError} style={{ padding: 0, color: 'red' }}>
                        {t('eos.landing_page.communication_channels.errors.field_required', { field: CHANNEL_VALIDATIONS_COPIES[channelError] })}
                      </li>
                    ),
                  )
                }
              </ul>
            </Grid>
          </Display>
        </Grid>

        <Display if={!privacyPolicy}>
          <Grid item xs={12} sx={{ mt: 3 }}>
            <FormGroup>
              <FormControlLabel
                control={<Checkbox checked={privacyPolicy}
                onChange={() => setPrivacyPolicy()} />}
                label={ReactHtmlParser(company.privacyPolicyText())}
                sx={{
                  'a': {
                    color: 'primary.main',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  },
                  [theme.breakpoints.only('xs')]: {
                    alignItems: 'start'
                  }
                }}
              />
            </FormGroup>
          </Grid>
        </Display>

        <Grid container sx={{ display: 'flex', justifyContent: 'center' }}>
          <Grid
            item
            md={6}
            xs={12}
            sx={{
              display: 'flex', mt: 4, justifyContent: 'center', flexDirection: 'column',
            }}
          >
            <Grid sx={{ display: 'flex', justifyContent: 'center' }}>
              <Display id="error" if={!isEmpty(dataErrors.toJS()) || error}>
                <Alert severity="error">
                  <AlertTitle>{t('eos.landing_page.label.error')}</AlertTitle>
                  {errorMessages()}
                </Alert>
              </Display>
            </Grid>
            <Grid sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
              <LoadingButton disabled={!isSubmittable} loading={loading} disableElevation variant="contained" color="primary" size="large" onClick={() => onSubmit()}>{t('eos.landing_page.label.get_started')}</LoadingButton>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <PrivacyPolicyPopUp show={showPopUp} onConfirm={setPrivacyPolicy} content={company.get('landingPagePopUpScreen')} footer={company.popUpPrivacyPolicyText()} />
    </Container>
  );
}

LandingPageApply.defaultProps = {
  navigate: undefined,
  uri:      undefined,
};

LandingPageApply.propTypes = {
  navigate: PropTypes.func,
  uri:      PropTypes.string,
};

export default LandingPageApply;
